<template>
  <div class="checklist-sub-item" :class="{ complete, active }">
    <tile>
      <div class="header">
        <div class="title d-flex flex-wrap">
          <span>
            <FontAwesomeIcon
              :class="iconConfiguration.class"
              :icon="iconConfiguration.icon" />&nbsp;{{ displayName }}&nbsp;{{
              formattedTitle
            }}&nbsp;<pill
              v-if="
                subitem?.applicantId &&
                subitem?.applicantId > 0 &&
                subitem?.processingCategoryId !== ProcessingCategoryIds.Income
              "
              color="gray position-absolute"
              :text="applicantTypeDisplayName"
              :maxHeight="'34px'"
          /></span>
        </div>
        <stipulation-text
          v-if="subitem.stipulationText && !complete"
          :subitem="subitem"
          class="hint"
        ></stipulation-text>
        <p v-if="subitem?.stipulationStatusName" class="subitem-status">
          {{ itemComplete && complete ? "Complete!" : "" }}
        </p>
      </div>

      <div
        v-if="
          subitem?.isExpectedDocument &&
          !complete &&
          !pending &&
          subitem?.processingCategoryName !== 'Income'
        "
        class="check-content upload"
      >
        <inline-uppy-uploader
          :ref="`${subitem?.applicantId}_2_${subitem?.id}`"
          :name="`${subitem?.applicantId}_2_${subitem?.id}`"
          :applicant="applicant"
          :applicationId="subitem?.applicationId"
          :documentTypeId="subitem.docTypeId ? subitem.docTypeId : 23"
          :stipulationId="subitem?.id"
          v-model="files[`${subitem?.applicantId}_2_${subitem?.id}`]"
          @uploadSuccess="handleUpload(subitem)"
        />
      </div>
      <div
        v-if="
          subitem?.isExpectedDocument &&
          !complete &&
          !pending &&
          subitem?.processingCategoryId === ProcessingCategoryIds.Income
        "
      >
        <div v-show="subitem?.stipulationTypeId === IncomeStipulationTypeIds.OtherIncome">
          Alimony, child support, or separate maintenance income need not be revealed if you do not
          wish to have it considered as a basis for repaying this obligation.
        </div>
        <uppy-uploader
          :ref="`${subitem?.applicantId}_1_${subitem?.id}`"
          v-model="files[`${subitem?.applicantId}_1_${subitem?.id}`]"
          :name="`${subitem?.applicantId}_1_${subitem?.id}`"
          :applicant="applicant"
          :show-toggle="true"
          :documentTypeId="subitem.docTypeId ? subitem.docTypeId : 23"
          :stipulationId="subitem?.id"
          :label="subitem?.stipTypeDescription"
          @uploadSuccess="handleUpload(subitem)"
        />
      </div>
      <div class="check-content">
        <e-sign
          v-if="
            subitem?.stipulationTypeId === EsignStipulationTypeIds.ExecuteLegalDocPackage ||
            subitem?.stipulationTypeId === EsignStipulationTypeIds.ConfirmationEsign
          "
          @esignClicked="$emit('esignClicked')"
        />
        <address-mismatch
          v-if="
            subitem?.stipulationTypeId === CreditStipulationTypeIds.ADDMismatch &&
            !complete &&
            !pending
          "
          :stip="subitem"
          :applicant="applicant"
          @success="handleStipSuccess"
        />
        <membership
          v-if="
            !complete &&
            !pending &&
            subitem?.stipulationTypeId === MembershipStipulationTypeIds.EstablishMembership
          "
          :stip="subitem"
          :applicant="assignedApplicant"
          @success="handleStipSuccess"
        />
        <name-input
          v-if="
            !complete &&
            !pending &&
            subitem?.stipulationTypeId === IdentityStipulationTypeIds.NameVerification
          "
          :stip="subitem"
          :applicant="applicant"
          @success="handleStipSuccess"
        />
        <address-input
          v-if="
            !complete &&
            !pending &&
            subitem?.stipulationTypeId === IdentityStipulationTypeIds.AddressVerification
          "
          :stip="subitem"
          :applicant="applicant"
          @success="handleStipSuccess"
        />
        <date-of-birth-input
          v-if="
            !complete &&
            !pending &&
            subitem?.stipulationTypeId === IdentityStipulationTypeIds.DateOfBirthVerification
          "
          :stip="subitem"
          :applicant="applicant"
          @success="handleStipSuccess"
        />
        <social-security-number-input
          v-if="
            !complete &&
            !pending &&
            subitem?.stipulationTypeId === IdentityStipulationTypeIds.SSNVerification
          "
          :stip="subitem"
          :applicant="applicant"
          @success="handleStipSuccess"
        />
        <confirm-payoff-request-input
          v-if="
            !complete &&
            !pending &&
            subitem?.stipulationTypeId === PayoffVerificationStipulationTypeIds.ConfirmPayoffRequest
          "
          :stip="subitem"
          :applicant="applicant"
          @success="handleStipSuccess"
        />
        <refi-underpayment-consent
          v-if="
            !complete &&
            !pending &&
            subitem?.stipulationTypeId ===
              PayoffVerificationStipulationTypeIds.ResponsibilityForUnderPayment
          "
          :stip="subitem"
          :applicant="applicant"
          @success="handleUpdateConsentStipResponseSuccess"
        />
        <servicer-payoff-address-input
          v-if="
            !complete &&
            !pending &&
            subitem?.stipulationTypeId ===
              PayoffVerificationStipulationTypeIds.ServicerPayoffAddress
          "
          :stip="subitem"
          @success="handleStipSuccess"
        />
        <self-certification-details-input
          v-if="
            subitem?.stipulationTypeId === RegulatoryStipulationTypeIds.SelfCertificationDetails
          "
          :stip="subitem"
          @success="handleStipSuccess"
        />
        <AutomatedIncomeVerificationConsent
          v-if="
            !complete &&
            !pending &&
            subitem?.stipulationTypeId ===
              IncomeStipulationTypeIds.AutomatedIncomeVerificationConsent
          "
          :stip="subitem"
          :applicant="applicant"
        />
        <AutomatedIncomeVerification
          v-if="
            !complete &&
            !pending &&
            subitem?.stipulationTypeId === IncomeStipulationTypeIds.IncomeVerification
          "
          :stip="subitem"
          :applicant="applicant"
          @success="handleUpload(subitem)"
        />
      </div>
      <div class="footer"></div>
    </tile>
  </div>
</template>
<script setup lang="ts">
import Tile from "@/components/shared/Tile.vue";
import { useStore } from "vuex";
import { computed, reactive, toRefs } from "vue";
import type { Applicant } from "@/models/application";
import UppyUploader from "@/components/shared/UppyUploader.vue";
import InlineUppyUploader from "@/components/shared/InlineUppyUploader.vue";
import AddressMismatch from "@/components/checklist/address-mismatch/AddressMismatch.vue";
import ESign from "@/components/checklist/ESign.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Pill from "./shared/Pill.vue";
import StipulationText from "@/components/checklist/StipulationText.vue";
import useFormatter from "@/use/formatter";
import Membership from "@/components/checklist/Membership.vue";
import NameInput from "@/components/checklist/NameInput.vue";
import AddressInput from "@/components/checklist/AddressInput.vue";
import DateOfBirthInput from "@/components/checklist/DateOfBirthInput.vue";
import SocialSecurityNumberInput from "@/components/checklist/SocialSecurityNumberInput.vue";
import ConfirmPayoffRequestInput from "@/components/checklist/ConfirmPayoffRequestInput.vue";
import RefiUnderpaymentConsent from "@/components/checklist/RefiUnderpaymentConsent.vue";
import ServicerPayoffAddressInput from "@/components/checklist/ServicerPayoffAddressInput.vue";
import SelfCertificationDetailsInput from "@/components/checklist/SelfCertificationDetailsInput.vue";
import AutomatedIncomeVerificationConsent from "@/components/checklist/income-verification/AutomatedIncomeVerificationConsent.vue";
import AutomatedIncomeVerification from "@/components/checklist/income-verification/AutomatedIncomeVerification.vue";
import { useStipulation } from "@/composables/stipulations/useStipulation";
import type { Stipulation } from "@/models/opal";
import { useOidcStore } from "@/use/useOidcStore";
import {
  PayoffVerificationStipulationTypeIds,
  EsignStipulationTypeIds,
  RegulatoryStipulationTypeIds,
  ApplicantTypeIds,
  IncomeStipulationTypeIds,
  ProcessingCategoryIds,
  CreditStipulationTypeIds,
  MembershipStipulationTypeIds,
  IdentityStipulationTypeIds,
  StipulationStatusIds,
} from "@/config/constants";

const store = useStore();

interface Props {
  title: string;
  subitem: any;
  active: boolean;
  itemComplete: boolean;
}
const props = withDefaults(defineProps<Props>(), {});

const { subitem } = toRefs(props);

const stipulation = computed<Stipulation>(() => props.subitem);

const { stipulationText, isComplete, isPending, assignedApplicant, isApplicantStip } =
  useStipulation(stipulation);
const { formatFullName } = useFormatter();
const { oidcUser, oidcIsAuthenticated } = useOidcStore();

const emit = defineEmits([
  "esignClicked",
  "handleUploadSuccess",
  "incomeSourcesUpdated",
  "addressMismatchStipUpdated",
]);

const displayName = computed<string>(() => {
  return isApplicantStip.value ? `${fullName.value}` : `${currentApplicantLoggedInFullName.value}`;
});

const iconConfiguration = computed<any | undefined>(() => {
  switch (stipulation.value.stipulationStatusId) {
    case StipulationStatusIds.OPEN:
      return {
        icon: ["fas", "circle-exclamation"],
        class: "icon incomplete",
      };

    case StipulationStatusIds.COMPLETED:
    case StipulationStatusIds.CU_OVERRIDE:
      return {
        icon: ["fas", "circle-check"],
        class: "icon complete",
      };

    case StipulationStatusIds.PENDING:
      return {
        icon: ["fas", "clock"],
        class: "icon yellow",
      };
    default:
      return undefined;
  }
});

const active = computed(() => props?.active);

const complete = computed(() => isComplete.value);

const pending = computed(() => isPending.value);

const applicant = computed(() =>
  store.state.appData.applicants.find((a: Applicant) => a.id === props?.subitem.applicantId),
);

const currentApplicant = computed(() =>
  store.state.appData.applicants.find((a: Applicant) => a.userId === oidcUser?.value?.sub),
);

const coApplicantTypeDisplayName = computed(() => store.getters["coApplicantTypeDisplayName"]);

const applicantTypeDisplayName = computed<string>(() => getApplicantTypeDisplayName() || "");

const files = reactive({} as any);

const fullName = computed(() =>
  oidcIsAuthenticated.value ? `${formatFullName(assignedApplicant.value)}'s` : "",
);

const currentApplicantLoggedInFullName = computed(() =>
  oidcIsAuthenticated.value ? `${formatFullName(currentApplicant.value)}'s` : "",
);

const formattedTitle = computed(() => getTitle());

async function handleUpload(event: any) {
  emit("handleUploadSuccess", event);
}

function handleUpdateConsentStipResponseSuccess() {
  store.dispatch("checklistModule/getSortedItemCompleteStatusByTitle", props.title);
}

function handleStipSuccess(updatedStip?: Stipulation) {
  if (updatedStip) {
    subitem.value.stipulationStatusId = updatedStip?.stipulationStatusId;
    subitem.value.stipulationStatusName = updatedStip?.stipulationStatusName;

    store.dispatch("checklistModule/updateStipVuexState", {
      stipulation: updatedStip,
      title: props.title,
    });

    store.dispatch("checklistModule/getSortedChecklistItemStatusByTitle", props.title);
  } else {
    subitem.value.stipulationStatusId = StipulationStatusIds.PENDING;
    subitem.value.stipulationStatusName = "Pending";

    store.dispatch("checklistModule/updateStipVuexState", {
      stipulation: subitem.value,
      title: props.title,
    });
    store.dispatch("checklistModule/getSortedChecklistItemStatusByTitle", props.title);
  }
}

function getTitle() {
  if (props.subitem?.processingCategoryName === "Income") {
    const re = /\b(19|20)\d{2}\b/;
    let result = props.subitem?.stipulationText.match(re);

    if (props.subitem.stipulationTypeId === IncomeStipulationTypeIds.IncomeVerification) {
      return props.subitem.stipulationText;
    }

    if (result?.length > 0) {
      return props?.subitem?.stipulationTypeDescription;
    } else {
      return props.title;
    }
  } else {
    return props?.subitem?.stipulationTypeDescription;
  }
}

function getApplicantTypeDisplayName() {
  return assignedApplicant.value?.applicantTypeId === ApplicantTypeIds.PRIMARY_APPLICANT
    ? "Borrower"
    : coApplicantTypeDisplayName.value;
}
</script>
<style scoped src="@/styles/icons/base.css"></style>
<style lang="scss">
.checklist-sub-item {
  .header .title {
    font-weight: 700;
    font-size: 1.25rem;
  }
  .header .hint {
    font-weight: normal;
    font-size: 1rem;
  }
}

@media (min-width: 576px) {
}
@media (min-width: 768px) {
}
@media (min-width: 992px) {
}

@media screen and (max-width: 992px) {
  .checklist-sub-item {
    .header .title {
      word-break: break-word;
    }
  }
}
</style>
